/**
 * BusinessSwitcher Component
 *
 * Dropdown for switching between businesses for multi-business users.
 *
 * Two variants:
 * - `popover` (default): Standalone Popover + Command for search. Use in pages/headers.
 * - `inline`: Flat list of buttons without Popover. Use when embedded inside another
 *   Popover/Sheet (e.g. AccountContextCenter) to avoid nested Radix focus traps.
 *
 * Accessibility:
 * - aria-live announcements for business switches
 * - Proper focus management
 * - Keyboard navigation support
 * - 44px minimum touch targets
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Check, ChevronDown, Building2, Search, Crown, Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { RoleBadge } from '@/components/ui/RoleBadge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface BusinessSwitcherProps {
  className?: string;
  showRoleBadge?: boolean;
  /** 'popover' (default) = standalone dropdown. 'inline' = flat list for embedding in another popover/sheet. */
  variant?: 'popover' | 'inline';
  /** Callback fired after a successful business switch (e.g. to close parent menu) */
  onSwitchComplete?: () => void;
}

export function BusinessSwitcher({
  className,
  showRoleBadge = true,
  variant = 'popover',
  onSwitchComplete,
}: BusinessSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const {
    businessMemberships,
    activeBusiness,
    switchBusiness,
    loading,
    isSuperAdmin,
    activeOrg,
  } = useAuth();

  // Filter to only businesses in the active organization — never show cross-org businesses
  const orgBusinesses = useMemo(
    () => activeOrg
      ? businessMemberships.filter(bm => bm.orgId === activeOrg.orgId)
      : [],
    [activeOrg, businessMemberships],
  );

  // Return focus to trigger when popover closes (popover variant only)
  useEffect(() => {
    if (variant === 'popover' && !open && triggerRef.current) {
      const timer = setTimeout(() => triggerRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [open, variant]);

  // Only show if user has multiple businesses in this org
  const showSwitcher = orgBusinesses.length > 1 || isSuperAdmin;

  const handleSelect = useCallback(async (businessId: string) => {
    // Ignore if already active or mid-switch
    if (businessId === activeBusiness?.businessId || isSwitching) return;

    setIsSwitching(true);
    setSwitchingId(businessId);

    const success = await switchBusiness(businessId);

    if (success) {
      const newBusiness = orgBusinesses.find(b => b.businessId === businessId);
      toast.success(`Switched to ${newBusiness?.businessName || 'business'}`);
      // Close AFTER switch succeeds (fixes race condition — Story 1.3)
      setOpen(false);
      onSwitchComplete?.();
    } else {
      toast.error('Failed to switch business. Please try again.');
    }

    setIsSwitching(false);
    setSwitchingId(null);
  }, [activeBusiness?.businessId, isSwitching, switchBusiness, orgBusinesses, onSwitchComplete]);

  // Single business — just display the name
  if (!showSwitcher) {
    return activeBusiness ? (
      <div className={cn('flex items-center gap-2', className)}>
        <Building2 className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium truncate max-w-[150px]">
          {activeBusiness.businessName}
        </span>
        {showRoleBadge && <RoleBadge role={activeBusiness.role} size="sm" />}
      </div>
    ) : null;
  }

  // ─── Inline variant: flat list of buttons (no Popover, no Command) ───
  if (variant === 'inline') {
    return (
      <div className={cn('space-y-1', className)} role="listbox" aria-label="Select business">
        {orgBusinesses.map((bm) => {
          const isActive = activeBusiness?.businessId === bm.businessId;
          const isLoading = switchingId === bm.businessId;

          return (
            <button
              key={bm.businessId}
              role="option"
              aria-selected={isActive}
              disabled={isSwitching}
              onClick={() => handleSelect(bm.businessId)}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors min-h-[44px]',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                isActive
                  ? 'bg-accent font-medium'
                  : 'hover:bg-accent/50',
                isSwitching && !isLoading && 'opacity-50 cursor-not-allowed',
              )}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />
              ) : (
                <Check
                  className={cn(
                    'h-4 w-4 shrink-0 transition-opacity',
                    isActive ? 'opacity-100 text-primary' : 'opacity-0'
                  )}
                />
              )}
              <span className="truncate flex-1 text-left">{bm.businessName}</span>
              {showRoleBadge && <RoleBadge role={bm.role} size="sm" />}
            </button>
          );
        })}
      </div>
    );
  }

  // ─── Popover variant: standalone dropdown with search ───
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={`Select a business. Current: ${activeBusiness?.businessName || 'None selected'}`}
          aria-busy={isSwitching}
          disabled={loading || isSwitching}
          className={cn(
            'justify-between min-w-[200px] max-w-[280px] min-h-[44px]',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
            className
          )}
        >
          <div className="flex items-center gap-2 truncate">
            {isSwitching ? (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
            ) : (
              <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}
            <span className="truncate">
              {isSwitching ? 'Switching...' : (activeBusiness?.businessName ?? 'Select business')}
            </span>
            {isSuperAdmin && !isSwitching && (
              <Crown className="h-3 w-3 text-amber-500" />
            )}
          </div>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search business..." aria-label="Search businesses" />
          <CommandList>
            <CommandEmpty>No business found.</CommandEmpty>
            <CommandGroup heading="Your Businesses">
              {orgBusinesses.map((bm) => {
                const isActive = activeBusiness?.businessId === bm.businessId;
                const isLoading = switchingId === bm.businessId;

                return (
                  <CommandItem
                    key={bm.businessId}
                    value={bm.businessName}
                    onSelect={() => handleSelect(bm.businessId)}
                    disabled={isSwitching}
                    className="cursor-pointer min-h-[44px]"
                    aria-selected={isActive}
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 shrink-0 animate-spin mr-2" />
                    ) : (
                      <Check
                        className={cn(
                          'h-4 w-4 shrink-0 mr-2',
                          isActive ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                    )}
                    <span className="truncate flex-1">{bm.businessName}</span>
                    {showRoleBadge && <RoleBadge role={bm.role} size="sm" />}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
